import { Component, OnDestroy, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, map } from 'rxjs/operators';
import * as L from 'leaflet';

import { BurnListService, SiteTwinService, SiteTypeService } from 'src/api/services';
import { BurnList, BurnListRegion, BurnCoup, SiteTwin } from 'src/api/models';
import { MapComponent } from 'src/app/components/map/map.component';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { BurnListDataService } from 'src/service/burn-list-data.service';
import { ToastrService } from 'ngx-toastr';
import { AzureAuthService } from 'src/service/azure-auth.service';

@Component({
  selector: 'app-fire-foresight-burn-coups',
  templateUrl: './fire-foresight-burn-coups.component.html',
  styleUrls: ['./fire-foresight-burn-coups.component.css']
})
export class FireForesightBurnCoupsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('burnCoupsMap', { read: MapComponent, static: false }) burnCoupsMap: MapComponent;

  private destroy$ = new Subject<void>();
  private needsMapLoad: boolean = false;

  public loading: boolean = false;
  public burnLists: BurnList[] = [];
  public burnCoups: BurnCoup[] = [];
  public selectedRegionId: number | null = null;
  public selectedRegion: BurnListRegion | null = null;
  public burnListRegions: BurnListRegion[] = [];

  // View state
  public currentView: 'table' | 'map' = 'table';
  
  // Map-related properties
  public coupLayers: any = {};

  // Refresh interval (in milliseconds) - 30 seconds for TV display
  private refreshInterval: any;
  private readonly REFRESH_RATE = 5*60000;
  private readonly VIEW_MODE_STORAGE_KEY = 'burnCoupsViewMode';

  // Site twins for burn coups fuel moisture data
  public burnCoupSiteTwins: Map<number, SiteTwin> = new Map();
  public siteTwinsLoading: boolean = false;

  // Fuel moisture refresh
  private fuelMoistureRefreshInterval: any = null;
  private readonly FUEL_MOISTURE_REFRESH_MS = 2 * 60 * 1000; // 2 minutes

  constructor(
    private route: ActivatedRoute,
    private burnListService: BurnListService,
    private siteTwinService: SiteTwinService,
    private siteTypeService: SiteTypeService,
    public app: ApplicationConfigService,
    private toasterService: ToastrService,
    public azureAuthService: AzureAuthService,
    private burnListDataService: BurnListDataService
  ) {}

  ngOnInit(): void {
    console.log("FireForesightBurnCoupsComponent initialized");

    // Load saved view mode from localStorage
    const savedView = localStorage.getItem(this.VIEW_MODE_STORAGE_KEY) as 'table' | 'map';
    if (savedView === 'table' || savedView === 'map') {
      this.currentView = savedView;
    }

    // Subscribe to route parameters
    this.route.params
      .pipe(
        map(params => params['burnListRegionId'] ? parseInt(params['burnListRegionId']) : null),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(regionId => {
        this.selectedRegionId = regionId;
        this.loadData();
      });

    // Set up auto-refresh for TV display
    this.refreshInterval = setInterval(() => {
      this.loadData(true); // Silent refresh
    }, this.REFRESH_RATE);

    // Set up fuel moisture refresh timer
    this.startFuelMoistureRefreshTimer();
  }

  ngAfterViewInit(): void {
    
  }

  onMapInitComplete(): void {
    // If we're in map mode and data is already loaded, load the map
    if (this.needsMapLoad && this.currentView === 'map' && !this.loading) {
      setTimeout(() => this.loadCoupShapesOnMap(), 5000);
      this.needsMapLoad = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    if (this.fuelMoistureRefreshInterval) {
      clearInterval(this.fuelMoistureRefreshInterval);
      this.fuelMoistureRefreshInterval = null;
    }
  }

  loadData(silent: boolean = false): void {
    this.loading = true;

    // Make sure customerId is available
    if (this.app.customerId == null) {
      this.app.customerId = this.azureAuthService.userProfile.customerId;
    }

    // Load all data in parallel
    Promise.all([
      this.loadBurnLists(),
      this.loadBurnListRegions()
    ]).then(() => {
      this.collateBurnCoups();
      // Wait for fuel moisture data to load before proceeding
      return this.loadFuelMoistureForAllBurnCoups();
    }).then(() => {
      this.loading = false;
      
      // If map view is selected, try to load the map or set flag for ngAfterViewInit
      if (this.currentView === 'map') {
        if (this.burnCoupsMap && this.burnCoupsMap.map) {
          // Map is already initialized (e.g., during refresh), load immediately
          setTimeout(() => this.loadCoupShapesOnMap(), 100);
        } else {
          // Map not yet initialized, set flag for ngAfterViewInit
          this.needsMapLoad = true;
        }
      }
    }).catch(error => {
      console.error('Error loading burn coups data:', error);
      this.loading = false;
    });
  }

  async loadBurnLists(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.burnListDataService.getBurnLists(this.app.customerId).subscribe(
        (data) => {
          this.burnLists = data || [];
          resolve();
        },
        (error) => {
          console.error('Error loading burn lists:', error);
          reject(error);
        }
      );
    });
  }

  async loadBurnListRegions(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.burnListDataService.getBurnListRegions().subscribe(
        (regions) => {
          this.burnListRegions = regions || [];
          if (this.selectedRegionId) {
            this.selectedRegion = this.burnListRegions.find(r => r.id === this.selectedRegionId) || null;
          }
          resolve();
        },
        (error) => {
          console.error('Error loading burn list regions:', error);
          reject(error);
        }
      );
    });
  }

  collateBurnCoups(): void {
    this.burnCoups = [];
    
    // Filter burn lists by region if selected
    let filteredBurnLists = [...this.burnLists];
    if (this.selectedRegionId) {
      filteredBurnLists = filteredBurnLists.filter(bl => bl.burnListRegionId === this.selectedRegionId);
    }

    // Track seen coup identifiers (case-insensitive) to remove duplicates
    const seenIdentifiers = new Set<string>();

    // Collect all burn coups from all burn lists
    filteredBurnLists.forEach(burnList => {
      if (burnList.burnCoups && burnList.burnCoups.length > 0) {
        burnList.burnCoups.forEach(coup => {
          // Get coup identifier in lowercase for case-insensitive comparison
          const coupIdentifier = (coup.geoAsset?.identifier || 'Coup #' + coup.id).toLowerCase();
          
          // Only add coup if we haven't seen this identifier before
          if (!seenIdentifiers.has(coupIdentifier)) {
            seenIdentifiers.add(coupIdentifier);
            
            // Add burn list info to coup for reference
            (coup as any).burnListName = burnList.planName;
            (coup as any).burnListId = burnList.id;
            this.burnCoups.push(coup);
          } else {
            console.log(`Skipping duplicate coup: ${coup.geoAsset?.identifier || 'Coup #' + coup.id} (case-insensitive match)`);
          }
        });
      }
    });

    // Sort burn coups alphabetically by identifier
    this.burnCoups.sort((a, b) => {
      const aIdentifier = (a.geoAsset?.identifier || 'Coup #' + a.id).toUpperCase();
      const bIdentifier = (b.geoAsset?.identifier || 'Coup #' + b.id).toUpperCase();
      return aIdentifier.localeCompare(bIdentifier);
    });

    console.log(`Collated ${this.burnCoups.length} unique burn coups from ${filteredBurnLists.length} burn lists`);
  }

  loadFuelMoistureForAllBurnCoups(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.burnCoups.length === 0) {
        console.log('No burn coups to load fuel moisture for');
        resolve();
        return;
      }

      const isInitialLoad = this.burnCoupSiteTwins.size === 0;
      if (isInitialLoad) {
        this.siteTwinsLoading = true;
      }

      const siteTypeCode = 'Fuel Moisture';

      console.log(`Loading site twins for ${this.burnCoups.length} burn coups using bulk search...`);

      // Use bulk search to get all sites at once
      this.siteTypeService.SiteTypeSearch({
        customerId: this.app.customerId,
        type: siteTypeCode,
        searchSize: 1000,
        pageIndex: 0,
        filterOld: false
      }).subscribe(
        (sites) => {
          if (!sites || sites.length === 0) {
            console.log('No sites returned from bulk search');
            this.siteTwinsLoading = false;
            resolve();
            return;
          }

          console.log(`Bulk search returned ${sites.length} sites`);

          // Create a map of sites by siteUri for quick lookup
          const sitesMap = new Map<string, any>();
          sites.forEach(site => {
            if (site.siteUri) {
              sitesMap.set(site.siteUri.toLowerCase(), site);
            }
          });

          // Match sites to burn coups and store in burnCoupSiteTwins
          let matchedCount = 0;
          this.burnCoups.forEach(coup => {
            if (!coup.geoAsset || !coup.geoAsset.identifier) {
              return;
            }

            const siteTwinUri = coup.geoAsset.identifier.toLowerCase();
            const site = sitesMap.get(siteTwinUri);

            if (site && site.parameters) {
              // Convert site to SiteTwin format and store
              this.burnCoupSiteTwins.set(coup.id, site as SiteTwin);
              matchedCount++;
            }
          });

          this.siteTwinsLoading = false;
          console.log(`Matched ${matchedCount} site twins to burn coups`);
          resolve();
        },
        (error) => {
          console.error('Failed to load site twins via bulk search:', error);
          this.siteTwinsLoading = false;
          reject(error);
        }
      );
    });
  }

  startFuelMoistureRefreshTimer(): void {
    console.log('Starting fuel moisture refresh timer (2 minutes)');
    this.fuelMoistureRefreshInterval = setInterval(() => {
      this.loadFuelMoistureForAllBurnCoups().catch(error => {
        console.error('Error during fuel moisture refresh:', error);
      });
    }, this.FUEL_MOISTURE_REFRESH_MS);
  }

  // Fuel Moisture Methods
  getFuelMoistureValue(burnCoupId: number, shade?: boolean): string | null {
    const siteTwin = this.burnCoupSiteTwins.get(burnCoupId);
    if (!siteTwin || !siteTwin.parameters) {
      return null;
    }

    const keys = Object.keys(siteTwin.parameters);
    const fuelMoistureKeys = keys.filter(k =>
      k.toLowerCase().includes('fuel') && k.toLowerCase().includes('moisture')
    );

    for (const key of fuelMoistureKeys) {
      const parameter = siteTwin.parameters[key];
      if (!parameter || !parameter.latestValue || !parameter.latestValue.value) {
        continue;
      }

      const p = parameter as any;
      if (p.metaData && p.metaData['shade'] && p.metaData["unitType"] == "Fuel Moisture") {
        const isShaded = p.metaData['shade'].toLowerCase() === 'true';
        if (shade !== undefined && isShaded === shade) {
          return Math.round(p.latestValue.value).toString();
        }
      }
    }

    return null;
  }

  getFuelMoistureShadedValue(burnCoupId: number): string | null {
    return this.getFuelMoistureValue(burnCoupId, true);
  }

  getFuelMoistureUnshadedValue(burnCoupId: number): string | null {
    return this.getFuelMoistureValue(burnCoupId, false);
  }

  isFuelMoistureDataStale(burnCoupId: number, shade: boolean): boolean {
    const siteTwin = this.burnCoupSiteTwins.get(burnCoupId);
    if (!siteTwin || !siteTwin.parameters) {
      return false;
    }

    const keys = Object.keys(siteTwin.parameters);
    const fuelMoistureKeys = keys.filter(k =>
      k.toLowerCase().includes('fuel') && k.toLowerCase().includes('moisture')
    );

    for (const key of fuelMoistureKeys) {
      const parameter = siteTwin.parameters[key];
      if (!parameter || !parameter.latestValue) {
        continue;
      }

      const p = parameter as any;
      if (p.metaData && p.metaData['shade'] && p.metaData["unitType"] == "Fuel Moisture") {
        const isShaded = p.metaData['shade'].toLowerCase() === 'true';
        if (isShaded === shade && p.latestValue.timeStamp) {
          const dataTime = new Date(p.latestValue.timeStamp);
          const now = new Date();
          const sixHoursInMs = 6 * 60 * 60 * 1000;
          const timeDiff = now.getTime() - dataTime.getTime();
          return timeDiff > sixHoursInMs;
        }
      }
    }

    return false;
  }

  getFuelMoistureBadgeClassWithStaleCheck(burnCoupId: number, value: string | null, shade: boolean = false): string {
    if (!value) return 'bg-secondary';

    if (this.isFuelMoistureDataStale(burnCoupId, shade)) {
      return 'bg-light';
    }

    return this.getFuelMoistureBadgeClass(value, shade);
  }

  getFuelMoistureBadgeClass(value: string | null, shade: boolean = false): string {
    if (!value) return 'bg-secondary';

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'bg-secondary';

    if (shade) {
      if (numValue < 17) return 'bg-danger';
      return 'bg-success';
    } else {
      if (numValue < 9) return 'bg-danger';
      if (numValue < 14) return 'bg-success';
      if (numValue < 16) return 'bg-warning';
      return 'bg-danger';
    }
  }

  // SDI Methods
  getSDIValue(burnCoupId: number): string | null {
    const siteTwin = this.burnCoupSiteTwins.get(burnCoupId);
    if (!siteTwin || !siteTwin.parameters) {
      return null;
    }

    const keys = Object.keys(siteTwin.parameters);
    
    for (const key of keys) {
      const parameter = siteTwin.parameters[key];
      if (!parameter || !parameter.latestValue || !parameter.latestValue.value) {
        continue;
      }

      const p = parameter as any;
      if (p.metaData && p.metaData["unitType"] === "SDI") {
        return Math.round(p.latestValue.value).toString();
      }
    }

    return null;
  }

  getSDITimestamp(burnCoupId: number): string | null {
    const siteTwin = this.burnCoupSiteTwins.get(burnCoupId);
    if (!siteTwin || !siteTwin.parameters) {
      return null;
    }

    const keys = Object.keys(siteTwin.parameters);
    
    for (const key of keys) {
      const parameter = siteTwin.parameters[key];
      if (!parameter || !parameter.latestValue || !parameter.latestValue.timeStamp) {
        continue;
      }

      const p = parameter as any;
      if (p.metaData && p.metaData["unitType"] === "SDI") {
        return p.latestValue.timeStamp;
      }
    }

    return null;
  }

  isSDIDataStale(burnCoupId: number): boolean {
    const timestamp = this.getSDITimestamp(burnCoupId);
    if (!timestamp) {
      return false;
    }
    
    const dataTime = new Date(timestamp);
    const now = new Date();
    const fortyEightHoursInMs = 48 * 60 * 60 * 1000; // 48 hours in milliseconds
    const timeDiff = now.getTime() - dataTime.getTime();
    return timeDiff > fortyEightHoursInMs;
  }

  getSDIBadgeClass(value: string | null): string {
    if (!value) return 'bg-secondary';
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'bg-secondary';

    if (numValue > 100) return 'bg-danger';
    return 'bg-success';
  }

  getSDIBadgeClassWithStaleCheck(burnCoupId: number, value: string | null): string {
    if (!value) return 'bg-secondary';
    
    if (this.isSDIDataStale(burnCoupId)) {
      return 'bg-light';
    }
    
    return this.getSDIBadgeClass(value);
  }

  // Split coups into 4 equal groups for display in 4 columns
  getCoupGroups(): BurnCoup[][] {
    const groups: BurnCoup[][] = [[], [], [], []];
    
    this.burnCoups.forEach((coup, index) => {
      const groupIndex = index % 4;
      groups[groupIndex].push(coup);
    });
    
    return groups;
  }

  // View switching methods
  setView(view: 'table' | 'map'): void {
    this.currentView = view;
    // Save view preference to localStorage
    localStorage.setItem(this.VIEW_MODE_STORAGE_KEY, view);
    this.needsMapLoad = false; // Clear the flag since we're handling it here
    if (view === 'map') {
      // Load coups on map with a small delay to ensure map is ready
      setTimeout(() => this.loadCoupShapesOnMap(), 100);
    }
  }

  // Load coup shapes onto the map with colors based on fuel moisture and SDI
  loadCoupShapesOnMap(): void {
    if (!this.burnCoupsMap || !this.burnCoupsMap.map) {
      console.warn('Map not ready yet');
      return;
    }

    console.log('Loading coup shapes on map...');

    // Clear existing layers
    Object.values(this.coupLayers).forEach((layer: any) => {
      if (layer.polygon) layer.polygon.remove();
      if (layer.marker) layer.marker.remove();
    });
    this.coupLayers = {};

    const bounds: L.LatLngBounds[] = [];

    this.burnCoups.forEach(burnCoup => {
      // Check if geoAsset has geometry data
      if (!burnCoup.geoAsset || !burnCoup.geoAsset.geometryGeoJson) {
        console.warn(`Burn coup ${burnCoup.id} missing geometry data`);
        return;
      }

      try {
        const geoJson = typeof burnCoup.geoAsset.geometryGeoJson === 'string' 
          ? JSON.parse(burnCoup.geoAsset.geometryGeoJson) 
          : burnCoup.geoAsset.geometryGeoJson;

        if (!geoJson || (!geoJson.type && !geoJson.geometry)) {
          console.error(`Invalid GeoJSON for coup ${burnCoup.id}`);
          return;
        }

        // Determine color based on fuel moisture and SDI
        const color = this.getCoupColorForMap(burnCoup.id);
        const fillOpacity = burnCoup.disabled ? 0.4 : 0.7;

        const layer = L.geoJSON(geoJson, {
          style: {
            fillColor: color,
            weight: 2,
            opacity: 1,
            color: color,
            fillOpacity: fillOpacity
          },
          onEachFeature: (feature, layer) => {
            const popupContent = this.createCoupPopupContent(burnCoup);
            layer.bindPopup(popupContent);
          }
        });

        layer.addTo(this.burnCoupsMap.map);
        bounds.push(layer.getBounds());

        // Add circle marker at centroid with color based on fuel moisture/SDI
        const labelCenter = layer.getBounds().getCenter();
        const circleMarker = L.circleMarker(labelCenter, {
          radius: 16,
          fillColor: color,
          color: '#ffffff',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.9
        });

        // Bind the same popup to the circle marker
        circleMarker.bindPopup(this.createCoupPopupContent(burnCoup));
        circleMarker.addTo(this.burnCoupsMap.map);

        this.coupLayers[burnCoup.id] = {
          polygon: layer,
          marker: circleMarker
        };
      } catch (error) {
        console.error(`Error loading coup ${burnCoup.id} on map:`, error);
      }
    });

    // Fit map to show all coups
    if (bounds.length > 0) {
      const group = new L.FeatureGroup(bounds.map(b => L.rectangle(b)));
      this.burnCoupsMap.map.fitBounds(group.getBounds(), { padding: [50, 50] });
    } else {
      console.warn('No coup shapes loaded on map');
    }
  }

  // Get color for a coup on the map based on fuel moisture and SDI
  getCoupColorForMap(burnCoupId: number): string {
    const burnCoup = this.burnCoups.find(bc => bc.id === burnCoupId);
    
    // If disabled, return gray
    if (burnCoup?.disabled) {
      return '#999999';
    }

    // Get fuel moisture values
    const shadedValue = this.getFuelMoistureShadedValue(burnCoupId);
    const unshadedValue = this.getFuelMoistureUnshadedValue(burnCoupId);
    const sdiValue = this.getSDIValue(burnCoupId);

    // Check for stale data
    const shadedIsStale = shadedValue !== null && this.isFuelMoistureDataStale(burnCoupId, true);
    const unshadedIsStale = unshadedValue !== null && this.isFuelMoistureDataStale(burnCoupId, false);
    const sdiIsStale = sdiValue !== null && this.isSDIDataStale(burnCoupId);

    // Get badge classes (only for non-stale data)
    const shadedClass = (shadedValue !== null && !shadedIsStale) 
      ? this.getFuelMoistureBadgeClass(shadedValue, true) 
      : null;
    const unshadedClass = (unshadedValue !== null && !unshadedIsStale) 
      ? this.getFuelMoistureBadgeClass(unshadedValue, false) 
      : null;
    const sdiClass = (sdiValue !== null && !sdiIsStale) 
      ? this.getSDIBadgeClass(sdiValue) 
      : null;

    // Priority: red (danger) > orange (warning) > green (success)
    // Check if any metric is red
    if (shadedClass === 'bg-danger' || unshadedClass === 'bg-danger' || sdiClass === 'bg-danger') {
      return '#dc3545'; // Bootstrap danger red
    }

    // Check if any fuel moisture is orange (warning)
    if (unshadedClass === 'bg-warning') {
      return '#ffc107'; // Bootstrap warning orange
    }

    // If all available metrics are green, return green
    const hasGreen = shadedClass === 'bg-success' || unshadedClass === 'bg-success' || sdiClass === 'bg-success';
    if (hasGreen) {
      return '#28a745'; // Bootstrap success green
    }

    // If all data is stale or missing, return light gray
    if ((shadedValue === null || shadedIsStale) && 
        (unshadedValue === null || unshadedIsStale) && 
        (sdiValue === null || sdiIsStale)) {
      return '#d3d3d3'; // Light gray for no data
    }

    // Default to blue
    return '#3388ff';
  }

  // Create popup content for a coup on the map
  createCoupPopupContent(burnCoup: BurnCoup): string {
    const geoAsset = burnCoup.geoAsset;
    const identifier = geoAsset?.identifier || 'Coup #' + burnCoup.id;
    const shadedValue = this.getFuelMoistureShadedValue(burnCoup.id);
    const unshadedValue = this.getFuelMoistureUnshadedValue(burnCoup.id);
    const sdiValue = this.getSDIValue(burnCoup.id);

    const shadedClass = this.getFuelMoistureBadgeClassWithStaleCheck(burnCoup.id, shadedValue, true);
    const unshadedClass = this.getFuelMoistureBadgeClassWithStaleCheck(burnCoup.id, unshadedValue, false);
    const sdiClass = this.getSDIBadgeClassWithStaleCheck(burnCoup.id, sdiValue);

    const getColorFromClass = (className: string): string => {
      switch(className) {
        case 'bg-danger': return '#dc3545';
        case 'bg-warning': return '#ffc107';
        case 'bg-success': return '#28a745';
        case 'bg-light': return '#d3d3d3';
        default: return '#6c757d';
      }
    };

    return `
      <div style="min-width: 250px;">
        <b style="font-size: 14px; color: black;">${identifier.toUpperCase()}</b><br>
        ${geoAsset?.name ? '<span style="color: black;">' + geoAsset.name + '</span><br>' : ''}
        ${burnCoup.disabled ? '<span style="color: #dc3545; font-weight: bold;"><i class="bi bi-slash-circle"></i> DISABLED</span><br>' : ''}
        <hr style="margin: 8px 0;">
        <div style="margin-bottom: 8px;">
          <small style="font-weight: bold; color: #495057;"><i class="bi bi-droplet-fill"></i> Fuel Moisture</small><br>
          <span style="display: inline-block; margin-top: 4px; padding: 4px 8px; background-color: ${getColorFromClass(shadedClass)}; color: white; border-radius: 3px; font-size: 12px;">
            Shade: ${shadedValue !== null ? shadedValue + '%' : 'N/A'}
          </span>
          <span style="display: inline-block; margin-top: 4px; margin-left: 4px; padding: 4px 8px; background-color: ${getColorFromClass(unshadedClass)}; color: white; border-radius: 3px; font-size: 12px;">
            Open: ${unshadedValue !== null ? unshadedValue + '%' : 'N/A'}
          </span>
        </div>
        <div>
          <small style="font-weight: bold; color: #495057;"><i class="bi bi-sun-fill"></i> SDI</small><br>
          <span style="display: inline-block; margin-top: 4px; padding: 4px 8px; background-color: ${getColorFromClass(sdiClass)}; color: white; border-radius: 3px; font-size: 12px;">
            ${sdiValue !== null ? sdiValue : 'N/A'}
          </span>
        </div>
      </div>
    `;
  }
}
